import { useState } from 'react';
import { message, FormInstance } from 'antd';
import dayjs from 'dayjs';
import { FuelReceipt, Vehicle } from '../../types';
import { fuelReceiptApi } from '../../services/api';
import { formatNumberWithDotThousandCommaDecimal } from '../../utils/numberFormatter';

interface UseFuelReceiptFormParams {
  form: FormInstance;
  onSuccess: () => void;
  t: (key: string) => string;
  vehicles: Vehicle[];
}

export const useFuelReceiptForm = ({ form, onSuccess, t, vehicles }: UseFuelReceiptFormParams) => {
  const [formData, setFormData] = useState({
    liters: 0,
    pricePerLiter: 0,
    totalAmount: 0,
    odometer: 0
  });
  const [receiptImage, setReceiptImage] = useState<File | null>(null);
  const [processing, setProcessing] = useState(false);
  const [processResult, setProcessResult] = useState<any>(null);
  const [uploadError, setUploadError] = useState('');
  const [uploadKey, setUploadKey] = useState<number>(Date.now());

  const validateFile = (file: File): boolean => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'application/pdf'];
    
    if (!allowedTypes.includes(file.type)) {
      message.error(t('invalid.file.type') || 'Invalid file type. Please upload an image or PDF file.');
      return false;
    }
    
    if (file.size > maxSize) {
      message.error(t('file.too.large') || 'File is too large. Maximum size is 10MB.');
      return false;
    }
    
    return true;
  };

  const handleNumericChange = (field: keyof typeof formData, value: string) => {
    const raw = value.replace(/[^0-9.,]/g, '');

    if (!raw) {
      setFormData(prev => ({ ...prev, [field]: 0 }));
      if (field === 'liters' || field === 'pricePerLiter') {
        setTimeout(() => calculateTotal(), 0);
      }
      return;
    }

    let numericStr: string;
    if (raw.includes(',')) {
      const parts = raw.split(',');
      const intPart = parts[0].replace(/\./g, '');
      const decPart = parts.slice(1).join('').replace(/\./g, '');
      numericStr = intPart + (decPart ? '.' + decPart : '');
    } else {
      numericStr = raw.replace(/\./g, '');
    }

    const numValue = parseFloat(numericStr) || 0;
    setFormData(prev => ({ ...prev, [field]: numValue }));

    if (field === 'liters' || field === 'pricePerLiter') {
      setTimeout(() => calculateTotal(), 0);
    }
  };

  const calculateTotal = () => {
    const total = formData.liters * formData.pricePerLiter;
    setFormData(prev => ({ ...prev, totalAmount: total }));
    form.setFieldsValue({
      totalAmount: formatNumberWithDotThousandCommaDecimal(total)
    });
  };

  const handleProcessImage = async () => {
    if (!receiptImage) {
      setUploadError(t('no.file.selected') || 'No file selected');
      return;
    }

    if (!validateFile(receiptImage)) {
      return;
    }

    setProcessing(true);
    setUploadError('');
    try {
      const response = await fuelReceiptApi.processFuelReceipt(receiptImage);
      setProcessResult(response.data);
      message.success(t('image.processed.successfully') || 'Image processed successfully');
      
      if (response.data && response.data.suggestedFuelReceipt) {
        const suggested = response.data.suggestedFuelReceipt;
        form.setFieldsValue({
          vehicleId: suggested.vehicleId,
          refuelDate: suggested.refuelDate ? dayjs(suggested.refuelDate) : undefined,
          gasStation: suggested.gasStation,
          location: suggested.location,
          receiptNumber: suggested.receiptNumber,
          notes: suggested.notes,
          liters: suggested.liters !== undefined && suggested.liters !== null ? formatNumberWithDotThousandCommaDecimal(suggested.liters) : '',
          pricePerLiter: suggested.pricePerLiter !== undefined && suggested.pricePerLiter !== null ? formatNumberWithDotThousandCommaDecimal(suggested.pricePerLiter) : '',
          totalAmount: suggested.totalAmount !== undefined && suggested.totalAmount !== null ? formatNumberWithDotThousandCommaDecimal(suggested.totalAmount) : '',
          odometer: suggested.odometer !== undefined && suggested.odometer !== null ? formatNumberWithDotThousandCommaDecimal(suggested.odometer) : '',
        });
        setFormData({
          liters: suggested.liters || 0,
          pricePerLiter: suggested.pricePerLiter || 0,
          totalAmount: suggested.totalAmount || 0,
          odometer: suggested.odometer || 0
        });
      }
      if (response.data && response.data.vehicleMatch && response.data.vehicleMatch.licensePlate) {
        const matchedVehicle = vehicles.find(v => v.licensePlate === response.data.vehicleMatch.licensePlate);
        if (matchedVehicle) {
          form.setFieldsValue({ vehicleId: matchedVehicle.id });
        }
      }
    } catch (err: any) {
      setUploadError(err?.message || t('failed.to.process.image') || 'Failed to process image');
      message.error(err?.message || t('failed.to.process.image') || 'Failed to process image');
    } finally {
      setProcessing(false);
    }
  };

  const resetForm = () => {
    form.resetFields();
    setReceiptImage(null);
    setProcessResult(null);
    setUploadError('');
    setUploadKey(Date.now());
    setFormData({
      liters: 0,
      pricePerLiter: 0,
      totalAmount: 0,
      odometer: 0
    });
  };

  const loadReceiptForEdit = (receipt: FuelReceipt) => {
    const formDataValues = {
      ...receipt,
      refuelDate: dayjs(receipt.refuelDate),
      liters: receipt.liters !== undefined && receipt.liters !== null ? formatNumberWithDotThousandCommaDecimal(receipt.liters) : '',
      pricePerLiter: receipt.pricePerLiter !== undefined && receipt.pricePerLiter !== null ? formatNumberWithDotThousandCommaDecimal(receipt.pricePerLiter) : '',
      totalAmount: receipt.totalAmount !== undefined && receipt.totalAmount !== null ? formatNumberWithDotThousandCommaDecimal(receipt.totalAmount) : '',
      odometer: receipt.odometer !== undefined && receipt.odometer !== null ? formatNumberWithDotThousandCommaDecimal(receipt.odometer) : ''
    };
    form.setFieldsValue(formDataValues);
    setFormData({
      liters: receipt.liters || 0,
      pricePerLiter: receipt.pricePerLiter || 0,
      totalAmount: receipt.totalAmount || 0,
      odometer: receipt.odometer || 0
    });
    setReceiptImage(null);
    setProcessResult(null);
    setUploadError('');
    setUploadKey(Date.now());
  };

  return {
    formData,
    receiptImage,
    processing,
    processResult,
    uploadError,
    uploadKey,
    setReceiptImage,
    setProcessResult,
    setUploadError,
    handleNumericChange,
    handleProcessImage,
    resetForm,
    loadReceiptForEdit,
    validateFile
  };
};
